import { HttpClient, HttpHeaders } from '@angular/common/http';
import { map, Observable, switchMap } from 'rxjs';
import { Injectable } from '@angular/core';

import { InterceptorUtil, OCC_USER_ID_ANONYMOUS, OccEndpointsService, USE_CLIENT_TOKEN, UserIdService } from '@spartacus/core';

import {
  AdyenPaymentData,
  AdyenPaymentDisplayResult,
  AdyenPaymentMode,
  AdyenPaymentsDetailsRequest,
  AdyenPaypalUpdateOrderResponse,
  CustomDeliveryMode,
  DonationCampaignsResult
} from '../models';

@Injectable({
  providedIn: 'root'
})
export class AdyenService {
  constructor(
    private userIdService: UserIdService,
    private http: HttpClient,
    private occEndpointsService: OccEndpointsService
  ) {}

  getAdyenPaymentMethods(orderCode: string, paymentModeCode: string): Observable<AdyenPaymentMode> {
    return this.userIdService.takeUserId(false).pipe(
      switchMap((userId: string) => {
        const url = this.occEndpointsService.buildUrl('adyenMethods', {
          urlParams: { userId, orderCode },
          queryParams: { paymentModeCode }
        });

        let headers = new HttpHeaders();

        if (userId === OCC_USER_ID_ANONYMOUS) {
          headers = InterceptorUtil.createHeader(USE_CLIENT_TOKEN, true, headers);
        }

        return this.http.get<AdyenPaymentMode>(url, { headers });
      })
    );
  }

  getAdyenExpressCheckoutPaymentModes(cartCode: string, cartGuid: string, postalCode?: string): Observable<AdyenPaymentMode> {
    return this.userIdService.takeUserId(false).pipe(
      switchMap((userId: string) => {
        const cartId = (userId === OCC_USER_ID_ANONYMOUS) ? cartGuid : cartCode;
        const url = this.occEndpointsService.buildUrl('adyenExpressCheckoutPaymentModes', {
          urlParams: { userId, cartId },
          queryParams: { postalCode }
        });

        let headers = new HttpHeaders();

        if (userId === OCC_USER_ID_ANONYMOUS) {
          headers = InterceptorUtil.createHeader(USE_CLIENT_TOKEN, true, headers);
        }

        return this.http.get<any>(url, { headers }).pipe(
          map((response) =>response?.adyenPaymentModes[0])
        );
      })
    );
  }

  getAdyenExpressCheckoutDeliveryModes(cartCode: string, cartGuid: string, postalCode?: string): Observable<CustomDeliveryMode[]> {
    return this.userIdService.takeUserId(false).pipe(
      switchMap((userId: string) => {
        const cartId = (userId === OCC_USER_ID_ANONYMOUS) ? cartGuid : cartCode;
        const url = this.occEndpointsService.buildUrl('adyenExpressCheckoutDeliveryModes', {
          urlParams: { userId, cartId },
          queryParams: { postalCode }
        });

        let headers = new HttpHeaders();

        if (userId === OCC_USER_ID_ANONYMOUS) {
          headers = InterceptorUtil.createHeader(USE_CLIENT_TOKEN, true, headers);
        }

        return this.http.get<any>(url, { headers }).pipe(
          map((response) => response?.deliveryModes)
        );
      })
    );
  }

  expressCheckoutUpdatePaypalOrder(
    cartCode: string,
    cartGuid: string,
    payPalpalUpdateOrderRequestString: string,
    postalCode?: string
  ): Observable<AdyenPaypalUpdateOrderResponse> {
    return this.userIdService.takeUserId(false).pipe(
      switchMap((userId: string) => {
        const cartId = (userId === OCC_USER_ID_ANONYMOUS) ? cartGuid : cartCode;
        const url = this.occEndpointsService.buildUrl('adyenExpressCheckoutUpdatePaypalOrder', {
          urlParams: { userId, cartId },
          queryParams: { postalCode }
        });

        let headers = new HttpHeaders({
          'Content-Type': 'application/json'
        });

        if (userId === OCC_USER_ID_ANONYMOUS) {
          headers = InterceptorUtil.createHeader(USE_CLIENT_TOKEN, true, headers);
        }

        return this.http.post<AdyenPaypalUpdateOrderResponse>(url, payPalpalUpdateOrderRequestString, { headers });
      })
    );
  }

  processAdyenPayment(
    orderCode: string,
    paymentModeCode: string,
    adyenPaymentData: AdyenPaymentData
  ): Observable<AdyenPaymentDisplayResult> {
    return this.userIdService.takeUserId(false).pipe(
      switchMap((userId) => {
        const url = this.occEndpointsService.buildUrl('adyenProcess', {
          urlParams: { userId, orderCode },
          queryParams: { paymentModeCode }
        });
        let headers = new HttpHeaders();
        if (userId === OCC_USER_ID_ANONYMOUS) {
          headers = InterceptorUtil.createHeader(USE_CLIENT_TOKEN, true, headers);
        }
        return this.http.post<AdyenPaymentDisplayResult>(url, adyenPaymentData, { headers });
      })
    );
  }

  processAdyenPaypalPayment(
    cartCode: string,
    paymentModeCode: string,
    adyenPaymentData: AdyenPaymentData
  ): Observable<AdyenPaymentDisplayResult> {
    return this.userIdService.takeUserId(false).pipe(
      switchMap((userId) => {
        const url = this.occEndpointsService.buildUrl('adyenProcessPaypal', {
          urlParams: { userId, cartId: cartCode },
          queryParams: { paymentModeCode }
        });

        let headers = new HttpHeaders();

        if (userId === OCC_USER_ID_ANONYMOUS) {
          headers = InterceptorUtil.createHeader(USE_CLIENT_TOKEN, true, headers);
        }

        return this.http.post<AdyenPaymentDisplayResult>(url, adyenPaymentData, { headers });
      })
    );
  }

  processAdyenDonation(
    amount: number,
    donationCampaignId: string,
    orderCode: string,
    paymentModeCode: string,
  ): Observable<AdyenPaymentDisplayResult> {
    return this.userIdService.takeUserId(false).pipe(
      switchMap((userId) => {
        const url = this.occEndpointsService.buildUrl('adyenDonation', {
          urlParams: { userId, orderCode },
          queryParams: { amount, donationCampaignId, paymentModeCode }
        });
        let headers = new HttpHeaders();
        if (userId === OCC_USER_ID_ANONYMOUS) {
          headers = InterceptorUtil.createHeader(USE_CLIENT_TOKEN, true, headers);
        }
        return this.http.post<AdyenPaymentDisplayResult>(url, { headers });
      })
    );
  }

  getAdyenDonationCampaigns(orderCode: string, paymentModeCode: string): Observable<DonationCampaignsResult> {
    return this.userIdService.takeUserId(false).pipe(
      switchMap((userId) => {
        const url = this.occEndpointsService.buildUrl('adyenDonationCampaigns', {
          urlParams: { userId, orderCode },
          queryParams: { paymentModeCode }
        });
        let headers = new HttpHeaders();
        if (userId === OCC_USER_ID_ANONYMOUS) {
          headers = InterceptorUtil.createHeader(USE_CLIENT_TOKEN, true, headers);
        }
        return this.http.post<DonationCampaignsResult>(url, { headers });
      })
    );
  }

  sendAdyenAdditionalDetails(
    orderCode: string,
    paymentModeCode: string,
    adyenPaymentsDetailsRequest: AdyenPaymentsDetailsRequest
  ): Observable<AdyenPaymentDisplayResult> {
    return this.userIdService.takeUserId(false).pipe(
      switchMap((userId) => {
        const url = this.occEndpointsService.buildUrl('adyenDetails', {
          urlParams: { userId, orderCode },
          queryParams: { paymentModeCode }
        });
        let headers = new HttpHeaders();
        if (userId === OCC_USER_ID_ANONYMOUS) {
          headers = InterceptorUtil.createHeader(USE_CLIENT_TOKEN, true, headers);
        }
        return this.http.post<AdyenPaymentDisplayResult>(url, adyenPaymentsDetailsRequest, { headers });
      })
    );
  }

  continueAdyenPayment(orderCode: string, redirectResult?: string): Observable<AdyenPaymentDisplayResult> {
    const url = this.occEndpointsService.buildUrl('adyenContinue', {
      urlParams: { orderCode },
      queryParams: { redirectResult }
    });
    return this.http.post<AdyenPaymentDisplayResult>(url, undefined);
  }
}
